// src/components/adminpanel/OrganizationsTab.tsx
import { useState, useEffect } from "react";
import { useOrganizationsStore } from "@/store/organizationsStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Building2, Users } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { Organization } from "@/types/organization";

export default function OrganizationsTab() {
  const { 
    organizations, 
    loading, 
    createOrganization, 
    deleteOrganization 
  } = useOrganizationsStore();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgShortName, setNewOrgShortName] = useState("");
  const [newOrgCity, setNewOrgCity] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Сброс формы
  useEffect(() => {
    if (!showCreateForm) {
      setNewOrgName("");
      setNewOrgShortName("");
      setNewOrgCity("");
    }
  }, [showCreateForm]);

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || !newOrgShortName.trim()) {
      toast({
        title: "Ошибка",
        description: "Название организации и короткое имя обязательны",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);

    const orgId = await createOrganization(
      newOrgName.trim(),
      newOrgShortName.trim(),
      newOrgCity.trim()
    );

    setIsCreating(false);

    if (orgId) {
      setShowCreateForm(false);
      toast({
        title: "✅ Организация успешно создана!",
        description: `${newOrgShortName} + структура готова`,
      });
    }
  };

  const handleDelete = async (orgId: string, shortName: string) => {
    if (!confirm(`Удалить организацию "${shortName}" и все связанные данные?\n\nЭто действие необратимо!`)) {
      return;
    }
    await deleteOrganization(orgId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 p-6 md:p-10">
      <div className="max-w-7xl mx-auto">

        {/* Заголовок */}
        <div className="flex justify-between items-end mb-12">
          <div>
            <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl px-8 py-5 mb-6">
              <Building2 className="h-12 w-12 text-purple-400" />
              <h1 className="text-5xl font-bold text-white tracking-tighter">Организации</h1>
            </div>
            <p className="text-purple-300 text-2xl">Управление рабочими пространствами подразделений</p>
          </div>

          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-10 py-6 rounded-3xl text-lg flex items-center gap-3 shadow-xl shadow-emerald-600/30"
          >
            <Plus className="h-6 w-6" />
            Новая организация
          </Button>
        </div>

        {/* Форма создания */}
        {showCreateForm && (
          <div className="bg-slate-900/90 backdrop-blur-2xl border border-purple-700/50 rounded-3xl p-10 mb-12">
            <h3 className="text-3xl font-bold text-white mb-8">Создание новой организации</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="text-purple-300 text-sm mb-2 block">Полное название организации</label>
                <Input
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="Отдел регионального взаимодействия ВСКС — Санкт-Петербург"
                  className="bg-slate-800/70 border-purple-600 text-white h-14 text-lg rounded-2xl"
                />
              </div>

              <div>
                <label className="text-purple-300 text-sm mb-2 block">Короткое название</label>
                <Input
                  value={newOrgShortName}
                  onChange={(e) => setNewOrgShortName(e.target.value)}
                  placeholder="ОРВ ВСКС СПб"
                  className="bg-slate-800/70 border-purple-600 text-white h-14 text-lg rounded-2xl"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-purple-300 text-sm mb-2 block">Город (необязательно)</label>
                <Input
                  value={newOrgCity}
                  onChange={(e) => setNewOrgCity(e.target.value)}
                  placeholder="Санкт-Петербург"
                  className="bg-slate-800/70 border-purple-600 text-white h-14 text-lg rounded-2xl"
                />
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <Button 
                onClick={handleCreateOrg} 
                disabled={isCreating || !newOrgName.trim() || !newOrgShortName.trim()}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-12 py-6 rounded-3xl text-lg flex-1"
              >
                {isCreating ? "Создаём..." : "Создать организацию"}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowCreateForm(false)}
                className="border-purple-600 text-purple-300 hover:bg-purple-900/50 px-12 py-6 rounded-3xl text-lg"
              >
                Отмена
              </Button>
            </div>
          </div>
        )}

        {/* Список организаций */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.length === 0 && !loading && (
            <div className="col-span-full text-center py-32 text-purple-400 text-xl">
              Организаций пока нет. Создайте первую.
            </div>
          )}

          {organizations.map((org) => (
            <div
              key={org.id}
              className="group bg-slate-900/90 backdrop-blur-2xl border border-purple-700/50 hover:border-purple-500 rounded-3xl p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-600/20"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-9 w-9 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{org.shortName}</h3>
                    <p className="text-purple-300 text-base mt-1 line-clamp-2">{org.name}</p>
                  </div>
                </div>

                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  Активна
                </Badge>
              </div>

              {org.city && (
                <p className="text-purple-400 text-sm mb-8">
                  Город: <span className="text-white">{org.city}</span>
                </p>
              )}

              <Button
                variant="destructive"
                size="lg"
                onClick={() => handleDelete(org.id, org.shortName)}
                className="w-full h-14 rounded-2xl text-base hover:bg-red-600/90 transition-all"
              >
                <Trash2 className="mr-3 h-5 w-5" />
                Удалить организацию
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}